
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { SessionContextProvider } from '@supabase/auth-helpers-react';
import { supabase } from "@/integrations/supabase/client";
import LoginPage from "./components/auth/LoginPage";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import Index from "./pages/Index";
import SavedChats from "./pages/SavedChats";
import Settings from "./pages/Settings";
import Documents from "./pages/Documents";
import DeleteAccount from "./pages/DeleteAccount";
import Navigation from "./components/Navigation";
import Footer from "./components/Footer";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Feedback from "./pages/Feedback";
import Landing from "./pages/Landing";
import HowItWorks from "./pages/HowItWorks";
import FAQ from "./pages/FAQ";
import WhyTrustUs from "./pages/WhyTrustUs";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail";
import UserFeedback from "./pages/UserFeedback";
import WebsiteHelp from "./pages/WebsiteHelp";
import ProcesaDocumentos from "./pages/ProcesaDocumentos";
import FloatingChatBubble from "./components/website-help/FloatingChatBubble";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <SessionContextProvider supabaseClient={supabase}>
      <TooltipProvider>
        <BrowserRouter>
          <div className="min-h-screen flex flex-col">
            <Toaster />
            <Sonner />
            {/* Add the floating chat bubble */}
            <FloatingChatBubble />
            <Routes>
              {/* Public routes that don't require authentication */}
              <Route path="/" element={<Navigate to="/landing" replace />} />
              <Route path="/landing" element={<Landing />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/why-trust-us" element={<WhyTrustUs />} />
              <Route path="/website-help" element={
                <>
                  <Navigation />
                  <div className="flex-grow">
                    <WebsiteHelp />
                  </div>
                  <Footer />
                </>
              } />
              <Route path="/blog" element={
                <>
                  <Navigation />
                  <div className="flex-grow">
                    <Blog />
                  </div>
                  <Footer />
                </>
              } />
              <Route path="/blog/:slug" element={
                <>
                  <Navigation />
                  <div className="flex-grow">
                    <BlogDetail />
                  </div>
                  <Footer />
                </>
              } />
              <Route path="/documents" element={
                <>
                  <Navigation />
                  <div className="flex-grow">
                    <Documents />
                  </div>
                  <Footer />
                </>
              } />
              <Route path="/privacy-policy" element={
                <>
                  <Navigation />
                  <div className="flex-grow">
                    <PrivacyPolicy />
                  </div>
                  <Footer />
                </>
              } />
              <Route path="/user-feedback" element={
                <>
                  <Navigation />
                  <div className="flex-grow">
                    <UserFeedback />
                  </div>
                  <Footer />
                </>
              } />
              
              {/* Protected routes that require authentication */}
              <Route
                path="/chat"
                element={
                  <>
                    <Navigation />
                    <div className="flex-grow">
                      <ProtectedRoute>
                        <Index />
                      </ProtectedRoute>
                    </div>
                    <Footer />
                  </>
                }
              />
              <Route
                path="/procesa-documentos"
                element={
                  <>
                    <Navigation />
                    <div className="flex-grow">
                      <ProtectedRoute>
                        <ProcesaDocumentos />
                      </ProtectedRoute>
                    </div>
                    <Footer />
                  </>
                }
              />
              <Route
                path="/saved-chats"
                element={
                  <>
                    <Navigation />
                    <div className="flex-grow">
                      <ProtectedRoute>
                        <SavedChats />
                      </ProtectedRoute>
                    </div>
                    <Footer />
                  </>
                }
              />
              <Route
                path="/settings"
                element={
                  <>
                    <Navigation />
                    <div className="flex-grow">
                      <ProtectedRoute>
                        <Settings />
                      </ProtectedRoute>
                    </div>
                    <Footer />
                  </>
                }
              />
              <Route
                path="/feedback"
                element={
                  <>
                    <Navigation />
                    <div className="flex-grow">
                      <ProtectedRoute>
                        <Feedback />
                      </ProtectedRoute>
                    </div>
                    <Footer />
                  </>
                }
              />
              <Route 
                path="/delete-account" 
                element={
                  <>
                    <Navigation />
                    <div className="flex-grow">
                      <DeleteAccount />
                    </div>
                    <Footer />
                  </>
                } 
              />
              
              {/* Catch all unknown routes and redirect to landing */}
              <Route path="*" element={<Navigate to="/landing" replace />} />
            </Routes>
          </div>
        </BrowserRouter>
      </TooltipProvider>
    </SessionContextProvider>
  </QueryClientProvider>
);

export default App;
